import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const KEY = "spicy3d_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(saved)
        ? saved.filter((item) => (
          item
          && typeof item === "object"
          && ["key", "product_id", "slug", "name", "color"].every(
            (field) => typeof item[field] === "string" && item[field].trim(),
          )
          && Number.isFinite(item.price)
          && Number.isFinite(item.quantity)
          && item.quantity > 0
        ))
        : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [code, setCodeState] = useState(() => localStorage.getItem("spicy3d_code") || "");
  const setCode = (c) => {
    setCodeState(c);
    if (c) localStorage.setItem("spicy3d_code", c); else localStorage.removeItem("spicy3d_code");
  };

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const value = useMemo(() => {
    const add = (product, color, quantity, max) => {
      const key = `${product.id}:${color}`;
      setItems((prev) => {
        const found = prev.find((i) => i.key === key);
        if (found) {
          return prev.map((i) => (i.key === key ? { ...i, max, quantity: Math.min(i.quantity + quantity, max) } : i));
        }
        return [
          ...prev,
          {
            key,
            product_id: product.id,
            slug: product.slug,
            name: product.name,
            color,
            price: product.price,
            shipping_price: product.shipping_price || 0,
            photo: product.cover_photo || product.photos?.[0] || "",
            quantity: Math.min(quantity, max),
            max,
          },
        ];
      });
      setOpen(true);
    };
    const update = (key, quantity) =>
      setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity: Math.max(1, Math.min(quantity, i.max || 50)) } : i)));
    const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
    const clear = () => { setItems([]); setCode(""); };
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const shipping = items.length ? 4.99 : 0;
    return { items, add, update, remove, clear, count, subtotal, shipping, open, setOpen, code, setCode };
  }, [items, open, code]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
